import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import * as Popover from "@radix-ui/react-popover";
import { joinClassNames } from "@utils/helpers";
import React, { useRef, useState } from "react";

interface Props {
  display?: string;
  value: string;
  options: Array<{
    display: string | React.ReactElement;
    value: string;
    is_hidden?: boolean;
  }>;
  onOptionSelect: ({
    option,
    index,
    display,
  }: {
    option: string;
    index: number;
    display?: string;
  }) => void;
  disabled?: boolean;
}

export default function PopoverSelect({
  display,
  value,
  options,
  onOptionSelect,
  disabled = false,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          ref={triggerRef}
          className="flex gap-1 h-9 w-fit items-center justify-between rounded-md shadow-sm border border-gray-300 px-3 py-2 text-lg focus:outline-none focus:ring-1 focus:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 focus:ring-indigo-500 bg-white hover:bg-gray-100 cursor-default placeholder:text-gray-600"
          disabled={disabled}
        >
          {display || value}
          <ChevronDownIcon className="w-4 h-4 opacity-50" />
        </Popover.Trigger>
        <Popover.Anchor className="absolute bottom-0 left-0" />
        <Popover.Content
          style={{
            minWidth: `calc(${triggerRef.current?.offsetWidth}px + 1.75rem)`,
          }}
          className="flex flex-col z-[999999999] font-normal p-1.5 bg-white border border-gray-300 rounded-md shadow-sm"
          align="start"
          alignOffset={-14}
          sideOffset={
            -37 -
            24 *
              options.findIndex((option) =>
                typeof option === "string"
                  ? option === value
                  : option.value === value
              )
          }
        >
          {options.map((option, index) => (
            <button
              id={index.toString()}
              type="button"
              key={option.value}
              className={joinClassNames(
                "relative flex items-center h-6 pl-6 text-lg leading-none text-black rounded cursor-default select-none pr-9 focus:outline-none",
                option.is_hidden ? "hidden" : ""
              )}
              onClick={() => {
                setOpen(false);
                onOptionSelect({
                  option: option.value,
                  index,
                  display:
                    typeof option.display === "string"
                      ? option.display
                      : option.value,
                });
              }}
            >
              {option.display || option.value}
              {option.value === value ? (
                <span className="absolute left-0 w-6 cursor-default">
                  <CheckIcon className="w-4 h-4" />
                </span>
              ) : (
                <></>
              )}
            </button>
          ))}
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
